import React, { useEffect } from 'react';

import { queryClient } from '@/data';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { AcceptMultiplierChanges, useAuthUserSettings, useUpdateUserSettings } from '@/hooks/use-auth-user-settings';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { logger } from '@/utils/logging';

import { CustomSwitch } from './CustomSwitch';

const LOG_TAG = '[multiplier change switch]: ';

export const AcceptMultiplierChangeSwitch = () => {
    const { data } = useAuthUserSettings();
    const { mutateAsync: updateUserSettings } = useUpdateUserSettings();
    const acceptAllMultiplierChanges = data?.accept_multiplier_changes === AcceptMultiplierChanges.ALL;
    const settings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.settings);
    const enabledHigherMultipliers = settings?.higher_multiplier_toggle_enabled;
    const updateBetSlipUserSettings = useSbkBetSlipStore(state => state.actions.updateUserSettings);
    const userSettingFromBetSlip = useSbkBetSlipStore(state => state.userSettings);
    useEffect(() => {
        if (data) {
            updateBetSlipUserSettings(data);
        }
    }, [acceptAllMultiplierChanges, data, updateBetSlipUserSettings]);

    const getAcceptMultiplierChangesValue = (value: boolean) => {
        if (!value) {
            return enabledHigherMultipliers ? AcceptMultiplierChanges.HIGHER : AcceptMultiplierChanges.NONE;
        }
        return AcceptMultiplierChanges.ALL;
    };

    const handleMultiplierChangeToggle = async () => {
        const newValueState = !acceptAllMultiplierChanges;
        const acceptMultiplierChangesValue = getAcceptMultiplierChangesValue(newValueState);
        if (newValueState) {
            GASbkEvents.reviewMultiplierChanges('toggle_on');
        }
        try {
            await updateUserSettings({ accept_multiplier_changes: acceptMultiplierChangesValue });
            await queryClient.invalidateQueries({ queryKey: authUserKeys.settings() });
        } catch (error: unknown) {
            logger.error(LOG_TAG, 'Error updating user settings', error);
            return;
        }
        if (userSettingFromBetSlip) {
            updateBetSlipUserSettings({
                ...userSettingFromBetSlip,
                accept_multiplier_changes: acceptMultiplierChangesValue,
            });
        }
    };

    return <CustomSwitch val={acceptAllMultiplierChanges} handleOnPress={handleMultiplierChangeToggle} />;
};
